import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../services/api';

const CashflowStatement = () => {
  const [cashflowData, setCashflowData] = useState(null);
  const [projections, setProjections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 1)), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    categoryId: '',
    flowType: '',
    status: ''
  });

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    fetchCashflowData();
    fetchCategories();
  }, [filters]);

  const fetchCashflowData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch cashflow statement
      const statementResponse = await fetch(
        `${API_BASE_URL}/cashflow/statement?startDate=${filters.startDate}&endDate=${filters.endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!statementResponse.ok) {
        throw new Error('Failed to fetch cashflow statement');
      }
      
      const statementData = await statementResponse.json();
      setCashflowData(statementData.data);

      // Fetch projections
      const projectionsResponse = await fetch(
        `${API_BASE_URL}/cashflow/projections?startDate=${filters.startDate}&endDate=${filters.endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (projectionsResponse.ok) {
        const projectionsData = await projectionsResponse.json();
        setProjections(projectionsData.data);
      }

      // Fetch transactions
      const transactionsResponse = await fetch(
        `${API_BASE_URL}/cashflow/transactions?startDate=${filters.startDate}&endDate=${filters.endDate}&categoryId=${filters.categoryId}&flowType=${filters.flowType}&status=${filters.status}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        setTransactions(transactionsData.data);
      }

    } catch (error) {
      console.error('Error fetching cashflow data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cashflow/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAutoCategorize = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cashflow/auto-categorize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchCashflowData(); // Refresh data
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error auto-categorizing:', error);
      alert('Error auto-categorizing transactions');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getFlowTypeColor = (flowType) => {
    return flowType === 'inflow' ? '#4CAF50' : '#F44336';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'reconciled': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Cashflow Statement
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.categoryId}
                  onChange={(e) => setFilters({...filters, categoryId: e.target.value})}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Flow Type</InputLabel>
                <Select
                  value={filters.flowType}
                  onChange={(e) => setFilters({...filters, flowType: e.target.value})}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="inflow">Inflow</MenuItem>
                  <MenuItem value="outflow">Outflow</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleAutoCategorize}
                fullWidth
                color="secondary"
              >
                Auto-Categorize
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Statement" />
          <Tab label="Projections" />
          <Tab label="Transactions" />
        </Tabs>
      </Box>

      {/* Cashflow Statement Tab */}
      {activeTab === 0 && cashflowData && (
        <Box>
          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Net Cashflow
                  </Typography>
                  <Typography variant="h4" color={cashflowData.netCashflow >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(cashflowData.netCashflow)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Operating Net
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(cashflowData.operating.total.net)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Investing Net
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(cashflowData.investing.total.net)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Financing Net
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(cashflowData.financing.total.net)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cashflow Statement Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cashflow Statement
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Inflow</TableCell>
                      <TableCell align="right">Outflow</TableCell>
                      <TableCell align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Operating Activities */}
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Operating Activities
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {cashflowData.operating.categories.map((category, index) => (
                      <TableRow key={`operating-${index}`}>
                        <TableCell sx={{ pl: 4 }}>{category.name}</TableCell>
                        <TableCell align="right">{formatCurrency(category.inflow)}</TableCell>
                        <TableCell align="right">{formatCurrency(category.outflow)}</TableCell>
                        <TableCell align="right">
                          <Typography color={category.net >= 0 ? 'success.main' : 'error.main'}>
                            {formatCurrency(category.net)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ pl: 2, fontWeight: 'bold' }}>Total Operating</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.operating.total.inflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.operating.total.outflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <Typography color={cashflowData.operating.total.net >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(cashflowData.operating.total.net)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Investing Activities */}
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Investing Activities
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {cashflowData.investing.categories.map((category, index) => (
                      <TableRow key={`investing-${index}`}>
                        <TableCell sx={{ pl: 4 }}>{category.name}</TableCell>
                        <TableCell align="right">{formatCurrency(category.inflow)}</TableCell>
                        <TableCell align="right">{formatCurrency(category.outflow)}</TableCell>
                        <TableCell align="right">
                          <Typography color={category.net >= 0 ? 'success.main' : 'error.main'}>
                            {formatCurrency(category.net)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ pl: 2, fontWeight: 'bold' }}>Total Investing</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.investing.total.inflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.investing.total.outflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <Typography color={cashflowData.investing.total.net >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(cashflowData.investing.total.net)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Financing Activities */}
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Financing Activities
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {cashflowData.financing.categories.map((category, index) => (
                      <TableRow key={`financing-${index}`}>
                        <TableCell sx={{ pl: 4 }}>{category.name}</TableCell>
                        <TableCell align="right">{formatCurrency(category.inflow)}</TableCell>
                        <TableCell align="right">{formatCurrency(category.outflow)}</TableCell>
                        <TableCell align="right">
                          <Typography color={category.net >= 0 ? 'success.main' : 'error.main'}>
                            {formatCurrency(category.net)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ pl: 2, fontWeight: 'bold' }}>Total Financing</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.financing.total.inflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(cashflowData.financing.total.outflow)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <Typography color={cashflowData.financing.total.net >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(cashflowData.financing.total.net)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Projections Tab */}
      {activeTab === 1 && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cashflow Projections
              </Typography>
              {projections.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={projections}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="total.inflow" stroke="#4CAF50" name="Projected Inflow" />
                    <Line type="monotone" dataKey="total.outflow" stroke="#F44336" name="Projected Outflow" />
                    <Line type="monotone" dataKey="total.net" stroke="#2196F3" name="Net Cashflow" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography>No projection data available</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Transactions Tab */}
      {activeTab === 2 && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cashflow Transactions
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Flow Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{format(new Date(transaction.transaction_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.category_name} 
                            size="small" 
                            color={transaction.category_type === 'operating' ? 'primary' : 
                                   transaction.category_type === 'investing' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell align="right">
                          <Typography color={transaction.flow_type === 'inflow' ? 'success.main' : 'error.main'}>
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.flow_type} 
                            size="small" 
                            color={transaction.flow_type === 'inflow' ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.status} 
                            size="small" 
                            color={getStatusColor(transaction.status)}
                          />
                        </TableCell>
                        <TableCell>{transaction.reference_number}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default CashflowStatement;
